import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  HardHat, MapPin, LogOut, Loader2, Camera, CheckCircle2, AlertTriangle, Clock,
  Smartphone, ShieldCheck, ListChecks, ChevronRight, ChevronLeft, Image as ImageIcon, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const POINTAGE_TOKEN_KEY = 'atlas_worker_token';
const POINTAGE_WORKER_KEY = 'atlas_worker_info';
const POINTAGE_DEVICE_KEY = 'atlas_worker_device_id';

const getOrCreateDeviceId = () => {
  let id = localStorage.getItem(POINTAGE_DEVICE_KEY);
  if (!id) {
    // UUID v4 compatible avec navigateurs sans crypto.randomUUID
    id = (crypto.randomUUID && crypto.randomUUID()) || (
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      })
    );
    localStorage.setItem(POINTAGE_DEVICE_KEY, id);
  }
  return id;
};

const Pointage = () => {
  const { shortcode } = useParams();
  const [token, setToken] = useState(localStorage.getItem(POINTAGE_TOKEN_KEY));
  const [worker, setWorker] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POINTAGE_WORKER_KEY)); } catch { return null; }
  });
  const [pin, setPin] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [today, setToday] = useState(null);
  const [loadingPtg, setLoadingPtg] = useState(null); // {chantier_id, type}
  const [photoB64, setPhotoB64] = useState(null);
  const [showCamera, setShowCamera] = useState(null); // {chantier_id, type}
  const fileRef = useRef(null);

  // Checklist state
  const [openChecklist, setOpenChecklist] = useState(null); // chantier object
  const [checklist, setChecklist] = useState(null);
  const [activeItem, setActiveItem] = useState(null); // {key}
  const [itemPhoto, setItemPhoto] = useState(null);
  const [itemNote, setItemNote] = useState('');
  const [itemLoading, setItemLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const itemFileRef = useRef(null);

  const api = axios.create();
  if (token) api.defaults.headers.common.Authorization = `Bearer ${token}`;
  // Identifiant appareil persistant (anti-fraude : 1 ouvrier = 1 appareil)
  const deviceId = getOrCreateDeviceId();
  api.defaults.headers.common['X-Device-Id'] = deviceId;

  // PWA install prompt
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsInstalled(standalone);
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    // Manifeste dédié au pointage (icône "ATLAS Pointage" sur l'écran d'accueil)
    let link = document.querySelector('link[rel="manifest"]');
    const previous = link?.getAttribute('href');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.setAttribute('href', '/manifest-pointage.json');
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      if (previous) link.setAttribute('href', previous);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) {
      toast.info("Ouvre le menu de ton navigateur puis 'Ajouter à l'écran d'accueil'");
      return;
    }
    installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice?.outcome === 'accepted') {
      toast.success('ATLAS Pointage est sur ton écran d\'accueil 🎉');
      setIsInstalled(true);
    }
    setInstallPrompt(null);
  };

  const logout = () => {
    localStorage.removeItem(POINTAGE_TOKEN_KEY);
    localStorage.removeItem(POINTAGE_WORKER_KEY);
    setToken(null);
    setWorker(null);
    setToday(null);
  };

  const login = async () => {
    if (pin.length !== 4) { toast.error('PIN à 4 chiffres'); return; }
    setLoginLoading(true);
    try {
      const r = await api.post('/api/team/worker-login', { shortcode: shortcode?.toUpperCase(), pin });
      localStorage.setItem(POINTAGE_TOKEN_KEY, r.data.token);
      localStorage.setItem(POINTAGE_WORKER_KEY, JSON.stringify(r.data.worker));
      setToken(r.data.token);
      setWorker(r.data.worker);
      setPin('');
      toast.dismiss();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail || 'Connexion impossible');
      }
    } finally { setLoginLoading(false); }
  };

  const loadToday = async () => {
    try {
      const r = await api.get('/api/team/worker/me/today');
      setToday(r.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  useEffect(() => { if (token) loadToday(); /* eslint-disable-next-line */ }, [token]);

  const getGPS = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({ lat: null, lng: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  });

  const distanceFrom = (lat, lng, ch) => {
    if (lat == null || ch?.gps_lat == null) return null;
    const R = 6371000;
    const phi1 = lat * Math.PI / 180; const phi2 = ch.gps_lat * Math.PI / 180;
    const dphi = (ch.gps_lat - lat) * Math.PI / 180; const dlam = (ch.gps_lng - lng) * Math.PI / 180;
    const a = Math.sin(dphi/2)**2 + Math.cos(phi1)*Math.cos(phi2)*Math.sin(dlam/2)**2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
  };

  // Live GPS for the assigned chantier
  const [livePos, setLivePos] = useState(null);
  useEffect(() => {
    if (!worker) return;
    getGPS().then(p => setLivePos(p));
  }, [worker]);

  const compressPhoto = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const max = 1024;
        let { width, height } = img;
        if (width > max || height > max) {
          if (width > height) { height = Math.round(height * max / width); width = max; }
          else { width = Math.round(width * max / height); height = max; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await compressPhoto(file);
      setPhotoB64(b64);
    } catch { toast.error('Photo invalide'); }
  };

  const submitPointage = async (chantier, type) => {
    setLoadingPtg({ chantier_id: chantier.id, type });
    try {
      const { lat, lng } = await getGPS();
      const r = await api.post('/api/team/worker/pointage', {
        chantier_id: chantier.id,
        type,
        gps_lat: lat, gps_lng: lng,
        photo_b64: photoB64,
      });
      if (r.data.flagged) toast.warning(`Pointage enregistré mais à ${r.data.distance_m}m du chantier (>${r.data.alert_threshold_m}m)`);
      else toast.success(type === 'debut' ? 'Début pointé. Bonne journée !' : 'Fin pointée. Bon retour.');
      setPhotoB64(null);
      setShowCamera(null);
      loadToday();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du pointage');
    } finally { setLoadingPtg(null); }
  };

  // ===== CHECKLIST FUNCTIONS =====
  const openChecklistFor = async (chantier) => {
    setOpenChecklist(chantier);
    setChecklist(null);
    try {
      const r = await api.get(`/api/team/worker/checklist/${chantier.id}`);
      setChecklist(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de charger la checklist');
      setOpenChecklist(null);
    }
  };

  const startEditItem = (it) => {
    setActiveItem(it);
    setItemPhoto(null);
    setItemNote(it.note || '');
  };

  const onItemPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const b64 = await compressPhoto(file);
      setItemPhoto(b64);
    } catch { toast.error('Photo invalide'); }
  };

  const saveChecklistItem = async (markDone) => {
    if (!activeItem || !openChecklist) return;
    if (markDone && activeItem.requires_photo && !itemPhoto && !activeItem.photo_id) {
      toast.error('Cet item nécessite une photo.');
      return;
    }
    setItemLoading(true);
    try {
      const r = await api.put(`/api/team/worker/checklist/${openChecklist.id}/item`, {
        key: activeItem.key,
        done: markDone,
        note: itemNote || null,
        photo_b64: itemPhoto || null,
      });
      setChecklist(c => ({
        ...c,
        items: c.items.map(x => x.key === activeItem.key ? r.data.item : x),
        complete: r.data.complete,
        missing: r.data.missing,
      }));
      setActiveItem(null);
      setItemPhoto(null);
      setItemNote('');
      toast.success(markDone ? 'Item validé' : 'Item enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec');
    } finally { setItemLoading(false); }
  };

  const validateChecklistByChef = async () => {
    if (!openChecklist || !checklist) return;
    setValidating(true);
    try {
      await api.post(`/api/team/worker/checklist/${openChecklist.id}/validate`);
      toast.success('Checklist validée. Le patron peut clôturer le chantier.');
      // refresh
      const r = await api.get(`/api/team/worker/checklist/${openChecklist.id}`);
      setChecklist(r.data);
      loadToday();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation refusée');
    } finally { setValidating(false); }
  };

  // ===== LOGIN SCREEN =====
  if (!token) {
    return (
      <div className="min-h-screen bg-atlasBg text-white flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="atlas-brand text-3xl text-atlasCyan tracking-[0.25em]">ATLAS</h1>
            <p className="text-xs text-atlasTextSecondary mt-1">Pointage chantier</p>
          </div>
          <div className="glass-card p-6 space-y-4">
            <p className="text-sm text-atlasTextSecondary text-center">Code équipe : <span className="text-atlasCyan font-mono font-semibold">{shortcode?.toUpperCase()}</span></p>
            <div>
              <label className="text-atlasTextSecondary text-sm">Ton PIN à 4 chiffres</label>
              <input
                data-testid="worker-pin-input"
                type="tel" inputMode="numeric" pattern="[0-9]*" maxLength={4}
                value={pin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0,4))}
                onKeyDown={e => e.key === 'Enter' && login()}
                className="w-full mt-2 text-center text-4xl font-mono tracking-[0.5em] py-4 bg-atlasBg border-2 border-atlasBorder focus:border-atlasCyan rounded-xl text-white outline-none"
                placeholder="••••"
                autoFocus
              />
            </div>
            <Button data-testid="worker-login-btn" onClick={login} disabled={loginLoading || pin.length !== 4} className="btn-primary w-full text-lg py-6">
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Se connecter'}
            </Button>
            <p className="text-[11px] text-atlasTextSecondary text-center">Demande ton PIN au patron si tu l'as oublié.</p>
            <div className="flex items-start gap-2 bg-atlasCyan/5 border border-atlasCyan/20 rounded-lg p-3 mt-2">
              <ShieldCheck className="w-4 h-4 text-atlasCyan flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-atlasTextSecondary leading-relaxed">
                Ton PIN est lié à <strong className="text-white">ce téléphone</strong>. Personne ne peut pointer à ta place depuis un autre appareil.
              </p>
            </div>
          </div>
          {!isInstalled && (
            <button
              data-testid="install-pwa-btn"
              onClick={triggerInstall}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-atlasCyan/10 border border-atlasCyan/30 text-atlasCyan hover:bg-atlasCyan/20 transition"
            >
              <Smartphone className="w-4 h-4" />
              Installer sur mon téléphone
            </button>
          )}
        </div>
      </div>
    );
  }

  // ===== TODAY SCREEN =====
  const current = today?.current_pointage;

  return (
    <div className="min-h-screen bg-atlasBg text-white pb-20">
      <header className="sticky top-0 z-10 bg-atlasBg/95 backdrop-blur border-b border-atlasBorder px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-xs text-atlasTextSecondary">Bonjour</p>
          <p className="text-lg font-semibold text-white">{worker?.name}</p>
        </div>
        <button onClick={logout} data-testid="worker-logout" className="text-atlasTextSecondary hover:text-white p-2"><LogOut className="w-5 h-5" /></button>
      </header>

      <div className="p-4 space-y-4">
        {!today ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-atlasCyan" /></div>
        ) : today.chantiers.length === 0 ? (
          <div className="glass-card p-6 text-center">
            <HardHat className="w-10 h-10 text-atlasTextSecondary mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Aucun chantier prévu aujourd'hui</p>
            <p className="text-xs text-atlasTextSecondary">{
              [0,6].includes(new Date().getDay())
                ? 'Bon week-end ! Reviens ici dès lundi pour pointer.'
                : 'Soit tu n\'es pas affecté, soit le chantier ne tourne pas ce jour. Contacte le patron en cas de doute.'
            }</p>
          </div>
        ) : (
          <>
            {current && (
              <div className={`glass-card p-4 border-l-4 flex items-center gap-3 ${today.forgotten_reminder ? (today.forgotten_reminder.severity === 'critical' ? 'border-l-red-500 bg-red-500/5' : 'border-l-orange-500 bg-orange-500/5') : 'border-l-atlasCyan'}`}>
                {today.forgotten_reminder ? <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${today.forgotten_reminder.severity === 'critical' ? 'text-red-400' : 'text-orange-400'}`} /> : <CheckCircle2 className="w-5 h-5 text-atlasCyan flex-shrink-0" />}
                <div className="text-sm flex-1 min-w-0">
                  {today.forgotten_reminder ? (
                    <>
                      <p className={`font-semibold ${today.forgotten_reminder.severity === 'critical' ? 'text-red-300' : 'text-orange-300'}`}>
                        Tu as oublié de pointer la fin ?
                      </p>
                      <p className="text-xs text-atlasTextSecondary">Pointé depuis {today.forgotten_reminder.elapsed_hours}h — pense à clôturer ta journée.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-atlasCyan font-medium">Tu es en pointage</p>
                      <p className="text-xs text-atlasTextSecondary">depuis {new Date(current.since).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}{current.elapsed_hours ? ` · ${current.elapsed_hours}h` : ''}</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {today.chantiers.map(c => {
              const dist = livePos ? distanceFrom(livePos.lat, livePos.lng, c) : null;
              const isThisActive = current?.chantier_id === c.id;
              const flaggedHint = dist != null && dist > 200;
              const canDebut = !current;
              const canFin = isThisActive;

              return (
                <div key={c.id} data-testid={`worker-chantier-${c.id}`} className="glass-card p-5 space-y-3">
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-white text-lg font-semibold">{c.title}</p>
                      {c.is_chef && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-atlasCyan/15 text-atlasCyan flex-shrink-0">Chef</span>}
                    </div>
                    {c.client_name && <p className="text-xs text-atlasTextSecondary">{c.client_name}</p>}
                    {c.address && <p className="text-xs text-atlasTextSecondary mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" />{c.address}</p>}
                  </div>

                  {dist != null && (
                    <div className={`text-xs flex items-center gap-2 px-3 py-2 rounded-md ${flaggedHint ? 'bg-orange-500/10 text-orange-300 border border-orange-500/30' : 'bg-atlasCyan/10 text-atlasCyan border border-atlasCyan/30'}`}>
                      {flaggedHint ? <AlertTriangle className="w-4 h-4" /> : <MapPin className="w-4 h-4" />}
                      <span>Tu es à <strong>{dist}m</strong> du chantier{flaggedHint && ' (hors zone)'}</span>
                    </div>
                  )}

                  {c.notes && <p className="text-xs text-atlasTextSecondary bg-white/[0.02] p-2 rounded">{c.notes}</p>}

                  {/* Checklist progress */}
                  {c.checklist_progress && c.checklist_progress.total > 0 && (
                    <button
                      data-testid={`open-checklist-${c.id}`}
                      onClick={() => openChecklistFor(c)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border ${c.checklist_progress.validated_at ? 'bg-emerald-500/10 border-emerald-500/30' : c.checklist_progress.done === c.checklist_progress.total ? 'bg-atlasCyan/10 border-atlasCyan/30' : 'bg-white/[0.02] border-atlasBorder hover:border-atlasCyan/30'}`}
                    >
                      <div className="flex items-center gap-2 text-left">
                        <ListChecks className={`w-4 h-4 ${c.checklist_progress.validated_at ? 'text-emerald-400' : 'text-atlasCyan'}`} />
                        <div>
                          <p className={`text-sm font-medium ${c.checklist_progress.validated_at ? 'text-emerald-300' : 'text-white'}`}>
                            {c.checklist_progress.validated_at ? 'Checklist validée ✓' : `Checklist (${c.checklist_progress.done}/${c.checklist_progress.total})`}
                          </p>
                          <p className="text-[11px] text-atlasTextSecondary">
                            {c.checklist_progress.validated_at ? 'Le patron peut clôturer le chantier' : 'À compléter au fil de la journée'}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-atlasTextSecondary" />
                    </button>
                  )}

                  {/* Photo option */}
                  {showCamera?.chantier_id === c.id && (
                    <div className="space-y-2">
                      <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhotoPick} className="hidden" />
                      {photoB64 ? (
                        <div className="relative">
                          <img src={photoB64} alt="Pointage" className="w-full rounded-lg max-h-48 object-cover" />
                          <button onClick={() => setPhotoB64(null)} className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded">✕</button>
                        </div>
                      ) : (
                        <Button onClick={() => fileRef.current?.click()} variant="outline" className="w-full border-atlasCyan/40 text-atlasCyan"><Camera className="w-4 h-4 mr-2" />Prendre une photo (optionnel)</Button>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      data-testid={`ptg-debut-${c.id}`}
                      disabled={!canDebut || loadingPtg !== null}
                      onClick={() => {
                        if (!showCamera) { setShowCamera({ chantier_id: c.id, type: 'debut' }); return; }
                        submitPointage(c, 'debut');
                      }}
                      className={`py-6 text-base font-semibold ${canDebut ? 'btn-primary' : 'bg-white/5 text-atlasTextSecondary'}`}
                    >
                      {loadingPtg?.chantier_id === c.id && loadingPtg.type === 'debut' ? <Loader2 className="w-5 h-5 animate-spin" /> : (showCamera?.chantier_id === c.id ? 'Confirmer début' : '▶ Pointer début')}
                    </Button>
                    <Button
                      data-testid={`ptg-fin-${c.id}`}
                      disabled={!canFin || loadingPtg !== null}
                      onClick={() => {
                        if (!showCamera) { setShowCamera({ chantier_id: c.id, type: 'fin' }); return; }
                        submitPointage(c, 'fin');
                      }}
                      className={`py-6 text-base font-semibold ${canFin ? 'bg-red-500/20 border border-red-500/50 text-red-300 hover:bg-red-500/30' : 'bg-white/5 text-atlasTextSecondary'}`}
                    >
                      {loadingPtg?.chantier_id === c.id && loadingPtg.type === 'fin' ? <Loader2 className="w-5 h-5 animate-spin" /> : (showCamera?.chantier_id === c.id ? 'Confirmer fin' : '■ Pointer fin')}
                    </Button>
                  </div>
                  {showCamera?.chantier_id === c.id && (
                    <button onClick={() => { setShowCamera(null); setPhotoB64(null); }} className="text-xs text-atlasTextSecondary hover:text-white w-full text-center">Annuler</button>
                  )}
                </div>
              );
            })}
          </>
        )}

        <div className="flex items-center justify-center gap-2 text-[11px] text-atlasTextSecondary pt-4">
          <Clock className="w-3 h-3" />
          {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} · {today?.today}
        </div>
      </div>

      {/* ====== CHECKLIST OVERLAY ====== */}
      {openChecklist && (
        <div className="fixed inset-0 bg-atlasBg z-50 overflow-y-auto" data-testid="checklist-overlay">
          {!activeItem ? (
            <>
              <header className="sticky top-0 bg-atlasBg/95 backdrop-blur border-b border-atlasBorder px-4 py-3 flex items-center justify-between gap-3">
                <button onClick={() => { setOpenChecklist(null); setChecklist(null); }} className="text-atlasTextSecondary hover:text-white -ml-2 p-2">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-atlasTextSecondary">Checklist qualité</p>
                  <p className="text-sm font-semibold text-white truncate">{openChecklist.title}</p>
                </div>
              </header>

              {!checklist ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-atlasCyan" /></div>
              ) : (
                <div className="p-4 space-y-3">
                  {checklist.validated_at ? (
                    <div className="glass-card p-4 border-l-4 border-l-emerald-500 bg-emerald-500/5 flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-300">Validée par le chef</p>
                        <p className="text-[11px] text-atlasTextSecondary">{new Date(checklist.validated_at).toLocaleString('fr-FR')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`p-3 rounded-lg border text-xs ${checklist.complete ? 'bg-atlasCyan/10 border-atlasCyan/30 text-atlasCyan' : 'bg-white/[0.02] border-atlasBorder text-atlasTextSecondary'}`}>
                      {checklist.complete
                        ? 'Tous les items sont OK. Le chef d\'équipe peut valider.'
                        : `${checklist.items.filter(i => i.done).length}/${checklist.items.length} items complétés.`}
                    </div>
                  )}

                  <ul className="space-y-2">
                    {checklist.items.map((it, idx) => (
                      <li key={it.key}>
                        <button
                          data-testid={`checklist-item-${it.key}`}
                          onClick={() => !checklist.validated_at && startEditItem(it)}
                          disabled={!!checklist.validated_at}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left ${it.done ? 'bg-atlasCyan/5 border-atlasCyan/30' : 'bg-white/[0.02] border-atlasBorder'} ${!checklist.validated_at ? 'hover:border-atlasCyan/50' : 'opacity-70 cursor-default'}`}
                        >
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${it.done ? 'bg-atlasCyan text-atlasBg' : 'bg-white/5 text-atlasTextSecondary border border-atlasBorder'}`}>
                            {it.done ? <Check className="w-4 h-4" /> : <span className="text-xs">{idx + 1}</span>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${it.done ? 'text-white' : 'text-white'}`}>{it.label}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {it.requires_photo && (
                                <span className={`text-[10px] flex items-center gap-1 ${it.photo_id ? 'text-atlasCyan' : 'text-orange-400'}`}>
                                  <Camera className="w-3 h-3" />
                                  {it.photo_id ? 'photo ✓' : 'photo requise'}
                                </span>
                              )}
                              {it.note && <span className="text-[10px] text-atlasTextSecondary truncate">📝 {it.note.slice(0, 30)}</span>}
                              {!it.required && <span className="text-[10px] text-atlasTextSecondary">(optionnel)</span>}
                            </div>
                          </div>
                          {!checklist.validated_at && <ChevronRight className="w-4 h-4 text-atlasTextSecondary flex-shrink-0" />}
                        </button>
                      </li>
                    ))}
                  </ul>

                  {!checklist.validated_at && checklist.is_chef && checklist.complete && (
                    <Button
                      data-testid="validate-checklist-btn"
                      onClick={validateChecklistByChef}
                      disabled={validating}
                      className="w-full py-6 bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/30 text-base font-semibold"
                    >
                      {validating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" />Valider la checklist (chef d'équipe)</>}
                    </Button>
                  )}
                  {!checklist.validated_at && !checklist.is_chef && checklist.complete && (
                    <p className="text-xs text-atlasTextSecondary text-center py-3">Le chef d'équipe doit valider la checklist pour la verrouiller.</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              <header className="sticky top-0 bg-atlasBg/95 backdrop-blur border-b border-atlasBorder px-4 py-3 flex items-center justify-between gap-3">
                <button onClick={() => { setActiveItem(null); setItemPhoto(null); }} className="text-atlasTextSecondary hover:text-white -ml-2 p-2">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-atlasTextSecondary">Item checklist</p>
                  <p className="text-sm font-semibold text-white truncate">{activeItem.label}</p>
                </div>
              </header>

              <div className="p-4 space-y-4">
                {activeItem.requires_photo && (
                  <div className="space-y-2">
                    <label className="text-atlasTextSecondary text-sm">Photo {activeItem.required ? '(obligatoire)' : '(optionnelle)'}</label>
                    <input ref={itemFileRef} type="file" accept="image/*" capture="environment" onChange={onItemPhotoPick} className="hidden" />
                    {itemPhoto ? (
                      <div className="relative">
                        <img src={itemPhoto} alt="" className="w-full rounded-lg max-h-64 object-cover" />
                        <button onClick={() => setItemPhoto(null)} className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded">✕</button>
                      </div>
                    ) : activeItem.photo_id ? (
                      <div className="space-y-2">
                        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-emerald-300 text-sm flex items-center gap-2">
                          <ImageIcon className="w-4 h-4" />Photo déjà enregistrée
                        </div>
                        <Button onClick={() => itemFileRef.current?.click()} variant="outline" className="w-full border-atlasCyan/40 text-atlasCyan"><Camera className="w-4 h-4 mr-2" />Remplacer la photo</Button>
                      </div>
                    ) : (
                      <Button onClick={() => itemFileRef.current?.click()} className="btn-primary w-full py-6 text-base"><Camera className="w-5 h-5 mr-2" />Prendre la photo</Button>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-atlasTextSecondary text-sm">Note (optionnel)</label>
                  <textarea
                    data-testid="item-note"
                    value={itemNote}
                    onChange={e => setItemNote(e.target.value)}
                    rows={3}
                    className="w-full mt-2 px-3 py-2 rounded-md bg-atlasBg border border-atlasBorder text-white text-sm outline-none focus:border-atlasCyan"
                    placeholder="Détail, anomalie, mesure…"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <Button onClick={() => saveChecklistItem(false)} disabled={itemLoading} variant="outline" className="border-atlasBorder text-white py-5">
                    {itemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                  </Button>
                  <Button
                    data-testid="item-validate"
                    onClick={() => saveChecklistItem(true)}
                    disabled={itemLoading || (activeItem.requires_photo && !itemPhoto && !activeItem.photo_id)}
                    className="btn-primary py-5"
                  >
                    {itemLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" />Valider</>}
                  </Button>
                </div>
                {activeItem.requires_photo && !itemPhoto && !activeItem.photo_id && (
                  <p className="text-[11px] text-orange-400 text-center mt-1">Photo requise pour valider cet item.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Pointage;
